import { useEffect, useState } from 'react';
import { supabase, isPlaceholderSupabase } from '../supabase';
import type { LoadState } from '../loadState';

export interface BrainMemoryRow {
  id: string;
  scope: 'coach' | 'team' | 'player' | 'session' | 'drill';
  kind: string;
  fact: string;
  tags: string[];
  confidence: number;
  created_at: string;
}

interface Result {
  rows: BrainMemoryRow[];
  state: LoadState;
  errorMessage?: string;
  refresh: () => void;
}

/** Live read of brain_memories for a coach, ordered newest-first.
 *  Scoped to the user's own memories; RLS handles access. */
export function useBrainMemories(coachId: string | null | undefined): Result {
  const [rows, setRows] = useState<BrainMemoryRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!coachId) {
      setRows([]);
      setState('empty');
      return;
    }
    if (isPlaceholderSupabase()) {
      setRows([]);
      setState('empty');
      return;
    }
    let cancelled = false;
    setState('loading');
    setErrorMessage(undefined);
    void (async () => {
      const { data, error } = await supabase
        .from('brain_memories')
        .select('id, scope, kind, fact, tags, confidence, created_at')
        .eq('coach_id', coachId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(80);
      if (cancelled) return;
      if (error) {
        setErrorMessage(error.message);
        setState('error');
        return;
      }
      const list = (data ?? []) as BrainMemoryRow[];
      setRows(list);
      setState(list.length > 0 ? 'ok' : 'empty');
    })();
    return () => {
      cancelled = true;
    };
  }, [coachId, refreshTick]);

  return { rows, state, errorMessage, refresh: () => setRefreshTick((n) => n + 1) };
}
